import React from 'react';
import { Button, Input, Checkbox, ControlLink, Overlay, Text } from '@moda/om';
import { noop } from '../../lib/noop';
import { usePasswordValidation } from '../../hooks/usePasswordValidation';
import { useAuth } from '../../hooks/useAuth';
import { useForm } from '../../hooks/useForm';
import { AuthAction, AuthPayload } from '../../lib/authenticate';
import { tc } from '../../lib/trackingContext';
import { deferUntilNewTitle } from '../../lib/deferUntilNewTitle';
import { SocialAuthProvider } from '../../hooks/useSocialAuth';
import { LoadingBalls } from '../LoadingBalls';
import { SocialAuthButton } from './SocialAuthButton';

type RegisterProps = {
  className: string;
  onAuth: (authUser?: AuthPayload) => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
};

type RegisterFormValues = {
  registerEmail: string;
  registerFirstName: string;
  registerLastName: string;
  registerPassword: string;
  registerRemember: boolean;
  registerEmailList: boolean;
};

const REGISTER_TITLE = 'Not a Member?';
const REGISTER_MESSAGE =
  'Create a Moda Operandi account to check your orders, save your information and keep your favorite products.';
const REGISTER_SUBMIT = 'REGISTER NOW';

const validations = {
  registerFirstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  registerLastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  },
  registerEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

export const Register: React.FC<RegisterProps> = ({
  className,
  hideAuthModal,
  showAuthModal,
  onAuth
}) => {
  const { error: errorAuth, handleAuth, loading } = useAuth(AuthAction.Register);
  const onSubmit = (values: RegisterFormValues) =>
    handleAuth({
      email: values.registerEmail,
      first_name: values.registerFirstName,
      last_name: values.registerLastName,
      password: values.registerPassword,
      remember: values.registerRemember,
      opted_in: values.registerEmailList
    })
      .then(onAuth)
      .then(() => deferUntilNewTitle(() => tc.track('Registration Completed')))
      .catch(noop);

  const { passwordValidation } = usePasswordValidation();

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: { ...validations, registerPassword: passwordValidation }
  });

  return (
    <>
      <div className={className} data-testid="registerSection">
        <form onSubmit={handleSubmit}>
          <div className="Authentication__title">{REGISTER_TITLE}</div>
          <div className="Authentication__message">{REGISTER_MESSAGE}</div>
          <div className="Authentication__error">{errorAuth?.message}</div>

          <div className="Authentication__fields">
            <div className="Authentication__alignedInputs">
              <Input
                className="Authentication__input"
                error={fieldErrors.registerFirstName?.message || errorAuth != null}
                id="registerFirstName"
                data-testid="registerFirstName"
                placeholder="First Name"
                type="text"
                maxLength={50}
              />

              <Input
                className="Authentication__input"
                error={fieldErrors.registerLastName?.message || errorAuth != null}
                id="registerLastName"
                data-testid="registerLastName"
                placeholder="Last Name"
                type="text"
                maxLength={50}
              />
            </div>

            <Input
              className="Authentication__input"
              error={fieldErrors.registerEmail?.message || errorAuth != null}
              id="registerEmail"
              data-testid="registerEmail"
              placeholder="Email"
              type="email"
            />

            <Input
              className="Authentication__input"
              error={fieldErrors.registerPassword?.message || errorAuth != null}
              id="registerPassword"
              data-testid="registerPassword"
              placeholder="Password"
              type="password"
            />

            <div className="Authentication__checkboxes">
              <Checkbox
                className="Authentication__checkbox"
                defaultChecked
                id="registerRemember"
                data-testid="registerRemember"
                type="checkbox"
              >
                Remember me
              </Checkbox>

              <Checkbox
                className="Authentication__checkbox"
                defaultChecked
                id="registerEmailList"
                data-testid="registerEmailList"
                type="checkbox"
              >
                Put me on the e-mail list for style inspiration &amp; special access
              </Checkbox>
            </div>

            <Button className="Authentication__button" type="submit" data-testid="registerSubmit">
              {REGISTER_SUBMIT}
            </Button>
          </div>
        </form>

        <div className="Authentication__divider" />

        <SocialAuthButton
          action={AuthAction.Register}
          provider={SocialAuthProvider.Amazon}
          showAuthModal={showAuthModal}
          hideAuthModal={hideAuthModal}
          onAuth={onAuth}
        />

        <SocialAuthButton
          action={AuthAction.Register}
          provider={SocialAuthProvider.Facebook}
          showAuthModal={showAuthModal}
          hideAuthModal={hideAuthModal}
          onAuth={onAuth}
        />

        <div className="Authentication__divider" />

        <div className="Authentication__optOut">
          <span>To opt-out of email sharing or to view our Privacy Policy, </span>
          <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank">
            click here
          </ControlLink>
        </div>
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
